import { Fragment, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Users,
  ChevronLeft,
  ChevronRight,
  Search,
  UserX,
  UserCheck,
  X,
} from 'lucide-react';
import Layout from '@/components/Layout';
import { AccountDisqualificationsList } from '@/components/campaigns/AccountDisqualificationsList';
import { LiftAccountDisqualificationDialog } from '@/components/campaigns/LiftAccountDisqualificationDialog';
import { ADMIN_CPM_CAMPAIGN_ACCOUNT_DISQUALIFICATIONS } from '@/queries/accountDisqualification';
import type { CpmAccountDisqualification } from '@/generated/account-disqualification';
import { groupAccountBansByUser } from '@/lib/campaigns/groupAccountBansByUser';
import CampaignHero from '@/components/CampaignHero';
import {
  ADMIN_CPM_CAMPAIGN,
  ADMIN_CPM_CAMPAIGN_PARTICIPANTS,
  ADMIN_DISQUALIFY_CPM_CAMPAIGN_USER,
  ADMIN_UNDISQUALIFY_CPM_CAMPAIGN_USER,
} from '@/queries/queries';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { formatDateTime, formatCompact } from '@/lib/format';

const PAGE_SIZE = 50;
const COL_SPAN = 8;

export default function AdminCampaignParticipantsPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuditorContext = location.pathname.startsWith('/campaign-audit');
  const backPath = isAuditorContext
    ? `/campaign-audit/${id}`
    : `/admin/campaigns/${id}`;

  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [disqualifiedOnly, setDisqualifiedOnly] = useState(false);
  const [page, setPage] = useState(1);

  const [banTarget, setBanTarget] = useState<string | null>(null);
  const [banReason, setBanReason] = useState('');
  const [unbanTarget, setUnbanTarget] = useState<string | null>(null);
  const [withAccountBan, setWithAccountBan] = useState(false);
  const [accountDialog, setAccountDialog] = useState<{ item: CpmAccountDisqualification; mode: 'lift' | 'restore' } | null>(null);

  const { data, loading, error, refetch } = useQuery(ADMIN_CPM_CAMPAIGN_PARTICIPANTS, {
    variables: {
      campaignId: id || '',
      search: search || undefined,
      disqualifiedOnly: disqualifiedOnly || undefined,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    },
    skip: !id,
    fetchPolicy: 'cache-and-network',
  });

  const { data: campaignData } = useQuery(ADMIN_CPM_CAMPAIGN, {
    variables: { id: id || '' },
    skip: !id,
  });
  const campaign = campaignData?.adminCpmCampaign;
  const currency = campaign?.currency || 'BRL';

  // Every ban of the campaign in one read: shown under each participant, and
  // the source of the "with a disqualified account" view, which therefore
  // covers every clipper rather than one page of participants.
  const {
    data: accountData, loading: accountLoading, error: accountError, refetch: refetchAccountBans,
  } = useQuery<{ adminCpmCampaignAccountDisqualifications: CpmAccountDisqualification[] }>(
    ADMIN_CPM_CAMPAIGN_ACCOUNT_DISQUALIFICATIONS,
    { variables: { campaignId: id || '' }, skip: !id, fetchPolicy: 'no-cache' },
  );
  const accountBans = accountData?.adminCpmCampaignAccountDisqualifications ?? [];
  const accountBansByUser = new Map<string, CpmAccountDisqualification[]>();
  for (const ban of accountBans) accountBansByUser.set(ban.user_id, [...(accountBansByUser.get(ban.user_id) ?? []), ban]);
  const accountBanGroups = withAccountBan ? groupAccountBansByUser(accountBans, search) : [];

  const formatEarnings = (cents: number) => {
    const value = (cents || 0) / 100;
    return new Intl.NumberFormat(currency === 'BRL' ? 'pt-BR' : 'en-US', {
      style: 'currency',
      currency,
    }).format(value);
  };

  const [disqualifyUser, { loading: banning }] = useMutation(ADMIN_DISQUALIFY_CPM_CAMPAIGN_USER);
  const [undisqualifyUser, { loading: unbanning }] = useMutation(ADMIN_UNDISQUALIFY_CPM_CAMPAIGN_USER);

   
  const participants = ((data?.adminCpmCampaignParticipants?.data as any[]) || []);
  const total = data?.adminCpmCampaignParticipants?.total ?? 0;
  const totalPages = data?.adminCpmCampaignParticipants?.pageCount ?? 1;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleBan = async (userId: string) => {
    if (!id || !banReason.trim()) return;
    try {
      const res = await disqualifyUser({
        variables: { campaignId: id, userId, reason: banReason.trim() },
      });
      const reclaimed = res?.data?.adminDisqualifyCpmCampaignUser?.reclaimed_cents ?? 0;
      toast({
        title: t('admin.campaigns.participants.banSuccess'),
        description: reclaimed > 0
          ? t('admin.campaigns.participants.banReclaimed', { amount: formatEarnings(reclaimed) })
          : undefined,
      });
      setBanTarget(null);
      setBanReason('');
      refetch();
     
    } catch (err: any) {
      toast({
        title: err?.message || t('admin.campaigns.participants.banError'),
        variant: 'destructive',
      });
    }
  };

  const handleUnban = async (userId: string) => {
    if (!id) return;
    try {
      await undisqualifyUser({ variables: { campaignId: id, userId } });
      toast({ title: t('admin.campaigns.participants.unbanSuccess') });
      setUnbanTarget(null);
      refetch();
     
    } catch (err: any) {
      toast({
        title: err?.message || t('admin.campaigns.participants.unbanError'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(backPath)}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                {t('common.back')}
              </Button>
              <div className="flex items-center gap-2">
                <Users className="w-6 h-6 text-primary" />
                <div>
                  <h1 className="text-xl font-bold">
                    {campaign?.name || t('admin.campaigns.participants.title')}
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    {total} {t('admin.campaigns.participants.countLabel')}
                  </p>
                </div>
              </div>
            </div>
            {!isAuditorContext && (
              <Badge variant="outline" className="text-red-500 border-red-500">ADMIN</Badge>
            )}
          </div>

          <CampaignHero campaign={campaign} />

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle>{t('admin.campaigns.participants.title')}</CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* The two filters are exclusive: the account view lists bans, not participants. */}
                  <Button
                    variant={disqualifiedOnly ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setDisqualifiedOnly((v) => !v); setWithAccountBan(false); setPage(1); }}
                  >
                    {t('admin.campaigns.participants.filters.disqualified')}
                  </Button>
                  <Button
                    variant={withAccountBan ? 'default' : 'outline'}
                    size="sm"
                    aria-pressed={withAccountBan}
                    onClick={() => { setWithAccountBan((v) => !v); setDisqualifiedOnly(false); setPage(1); }}
                  >
                    {t('admin.campaigns.accountDisqualification.filterWithAccount')}
                  </Button>
                  <form onSubmit={handleSearch} className="flex gap-1">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder={t('admin.campaigns.participants.searchPlaceholder')}
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        className="pl-8 max-w-xs h-9"
                      />
                    </div>
                    <Button type="submit" variant="outline" size="sm">
                      <Search className="w-4 h-4" />
                    </Button>
                  </form>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {withAccountBan ? (
                accountLoading && !accountData ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                  </div>
                ) : accountError ? (
                  <div className="text-center py-8 text-red-500">
                    {t('admin.common.error')}
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="account-ban-users">
                    {accountBanGroups.map((group) => (
                      <div key={group.userId} className="space-y-2 rounded-md border p-3">
                        <p className="font-medium text-sm">{group.userName || '—'}</p>
                        <AccountDisqualificationsList
                          items={group.bans}
                          onLift={(item) => setAccountDialog({ item, mode: 'lift' })}
                          onRestore={(item) => setAccountDialog({ item, mode: 'restore' })}
                        />
                      </div>
                    ))}
                    {accountBanGroups.length === 0 && (
                      <p className="py-10 text-center text-sm text-muted-foreground">
                        {t('admin.campaigns.accountDisqualification.filterWithAccountEmpty')}
                      </p>
                    )}
                  </div>
                )
              ) : loading && !data ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
              ) : error ? (
                <div className="text-center py-8 text-red-500">
                  {t('admin.common.error')}
                </div>
              ) : (
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('admin.campaigns.participants.table.user')}</TableHead>
                          <TableHead>{t('admin.campaigns.participants.table.submissions')}</TableHead>
                          <TableHead>{t('admin.campaigns.participants.table.approved')}</TableHead>
                          <TableHead>{t('admin.campaigns.participants.table.totalViews')}</TableHead>
                          <TableHead>{t('admin.campaigns.participants.table.earnings')}</TableHead>
                          <TableHead>{t('admin.campaigns.participants.table.firstSubmitted')}</TableHead>
                          <TableHead>{t('admin.campaigns.participants.table.status')}</TableHead>
                          <TableHead className="w-[140px]">
                            {t('admin.campaigns.participants.table.actions')}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {participants.map((p) => {
                          const totalViews = parseInt(p.total_views || '0', 10);
                          return (
                            <Fragment key={p.user_id}>
                              <TableRow className={p.disqualified ? 'opacity-60' : ''}>
                                <TableCell>
                                  <p className="font-medium text-sm">{p.user_name || '—'}</p>
                                  <p className="text-xs text-muted-foreground">{p.user_email || '—'}</p>
                                </TableCell>
                                <TableCell>{p.submission_count}</TableCell>
                                <TableCell>{p.approved_count}</TableCell>
                                <TableCell>{formatCompact(totalViews)}</TableCell>
                                <TableCell className="font-medium">
                                  {formatEarnings(p.total_earnings_cents)}
                                </TableCell>
                                <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                                  {p.first_submitted_at ? formatDateTime(p.first_submitted_at) : '—'}
                                </TableCell>
                                <TableCell>
                                  {p.disqualified ? (
                                    <div>
                                      <Badge variant="destructive" className="text-[10px]">
                                        {t('admin.campaigns.participants.statusDisqualified')}
                                      </Badge>
                                      {p.disqualification_reason && (
                                        <p className="text-xs text-red-400 mt-1">{p.disqualification_reason}</p>
                                      )}
                                    </div>
                                  ) : (
                                    <Badge variant="outline" className="text-[10px]">
                                      {t('admin.campaigns.participants.statusActive')}
                                    </Badge>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {p.disqualified ? (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-emerald-500"
                                      onClick={() => setUnbanTarget(p.user_id)}
                                      title={t('admin.campaigns.participants.actions.undisqualify')}
                                    >
                                      <UserCheck className="w-4 h-4 mr-1" />
                                      {t('admin.campaigns.participants.actions.undisqualify')}
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-500"
                                      onClick={() => { setBanTarget(p.user_id); setBanReason(''); }}
                                      title={t('admin.campaigns.participants.actions.disqualify')}
                                    >
                                      <UserX className="w-4 h-4 mr-1" />
                                      {t('admin.campaigns.participants.actions.disqualify')}
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                              {(accountBansByUser.get(p.user_id) ?? []).length > 0 && (
                                <TableRow>
                                  <TableCell colSpan={COL_SPAN}>
                                    <AccountDisqualificationsList
                                      items={accountBansByUser.get(p.user_id) ?? []}
                                      onLift={(item) => setAccountDialog({ item, mode: 'lift' })}
                                      onRestore={(item) => setAccountDialog({ item, mode: 'restore' })}
                                    />
                                  </TableCell>
                                </TableRow>
                              )}
                              {banTarget === p.user_id && (
                                <TableRow>
                                  <TableCell colSpan={COL_SPAN}>
                                    <div className="border border-red-500/30 rounded-lg p-3 space-y-2 bg-red-500/5">
                                      <p className="text-xs font-medium text-red-400">
                                        {t('admin.campaigns.submissions.banUserConfirm')}
                                      </p>
                                      <div className="space-y-1">
                                        <Label className="text-xs">
                                          {t('admin.campaigns.participants.banReasonLabel')}
                                        </Label>
                                        <Textarea
                                          value={banReason}
                                          onChange={(e) => setBanReason(e.target.value)}
                                          placeholder={t('admin.campaigns.participants.banReasonPlaceholder')}
                                          rows={2}
                                        />
                                      </div>
                                      <div className="flex gap-2">
                                        <Button
                                          variant="destructive"
                                          size="sm"
                                          onClick={() => handleBan(p.user_id)}
                                          disabled={banning || !banReason.trim()}
                                        >
                                          <UserX className="w-3 h-3 mr-1" />
                                          {t('admin.campaigns.participants.actions.disqualify')}
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => { setBanTarget(null); setBanReason(''); }}
                                        >
                                          <X className="w-3 h-3 mr-1" />
                                          {t('common.cancel')}
                                        </Button>
                                      </div>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </Fragment>
                          );
                        })}
                        {participants.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={COL_SPAN} className="text-center py-10 text-muted-foreground">
                              <p className="font-medium">{t('admin.campaigns.participants.emptyTitle')}</p>
                              <p className="text-xs mt-1">{t('admin.campaigns.participants.emptyDescription')}</p>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="flex items-center justify-between mt-4" data-testid="participants-pagination">
                    <span className="text-sm text-muted-foreground">
                      {t('admin.common.page')} {page} {t('admin.common.of')} {totalPages}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((pp) => Math.max(1, pp - 1))}
                        disabled={page === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((pp) => Math.min(totalPages, pp + 1))}
                        disabled={page >= totalPages}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <LiftAccountDisqualificationDialog
        disqualification={accountDialog?.item ?? null}
        mode={accountDialog?.mode ?? 'lift'}
        currency={currency}
        onOpenChange={(open) => { if (!open) setAccountDialog(null); }}
        onDone={() => { void refetch(); void refetchAccountBans(); }}
      />
      <AlertDialog
        open={!!unbanTarget}
        onOpenChange={(open) => { if (!open) setUnbanTarget(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('admin.campaigns.participants.actions.undisqualify')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.campaigns.submissions.unbanUserConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={unbanning}
              onClick={() => unbanTarget && handleUnban(unbanTarget)}
            >
              {t('common.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
